import type { Transaction } from '../types';
import { lookupRatesSync } from './fxRates';

/**
 * Rendering a typed amount back in the display currency.
 *
 * ── The bug this module exists to kill ───────────────────────────────────────
 *
 * A fee entered as 1.50 AUD displayed as A$1.51. The cause was not rounding: the
 * STORE leg converted AUD→USD at the rate for the transaction's own date (RBA,
 * 0.7102) while the DISPLAY leg converted USD→AUD back at TODAY's spot (1.42).
 * Those are different numbers — 0.85% apart on the day this was found — so they
 * did not cancel:
 *
 *     1.50 × 0.7102 × 1.42 = 1.5127  →  "A$1.51"
 *
 * The same arithmetic hit price, where it mattered far more because the error is
 * proportional: a 100.00 AUD price displayed as A$100.85.
 *
 * Storage was never wrong. The display leg was, and this is the fix.
 *
 * ── The rule ─────────────────────────────────────────────────────────────────
 *
 * Three tiers, in order:
 *
 *   1. RAW — the row recorded the amount as typed AND in the currency now on
 *      screen. Return it verbatim. No arithmetic, so no drift is even possible.
 *      This is the common case, and making it exact BY CONSTRUCTION rather than
 *      by cancelling rates is what makes this robust.
 *   2. DATED — convert the stored USD at the rate for the ROW'S OWN date. Right
 *      for a row entered in one currency and viewed in another.
 *   3. SPOT — today's rate, flagged `exact: false`. Only for rows with no FX
 *      capture and no resolvable date.
 */

export interface DisplayAmount {
  /** The amount, in the display currency. */
  value: number;
  /**
   * False when tier 3 was used, i.e. the figure was converted at today's rate
   * because nothing better existed. Carried so a caller can mark it.
   */
  exact: boolean;
}

/** Units of `displayCurrency` per 1 USD on `date`, or null if unresolvable. */
function perUsdOn(displayCurrency: string, date: string | undefined): number | null {
  if (!date) return null;
  const resolved = lookupRatesSync(displayCurrency, date);
  if (!resolved || !isFinite(resolved.fxRateUsd) || resolved.fxRateUsd <= 0) return null;
  // fxRateUsd is USD per unit of the currency; we want the reciprocal.
  return 1 / resolved.fxRateUsd;
}

/**
 * Shared three-tier resolution.
 *
 * `rawAmount` / `rawCurrency` are the as-typed pair (tier 1); `storedUsd` is the
 * derived USD cache (tiers 2 and 3). `rateDate` is the date the row's own rates
 * belong to, which may differ from the transaction date after a roll-back.
 */
function resolve(
  storedUsd: number,
  rawAmount: number | undefined,
  rawCurrency: string | undefined,
  rateDate: string | undefined,
  displayCurrency: string,
  spotPerUsd: number
): DisplayAmount {
  const display = (displayCurrency || 'USD').toUpperCase();

  // 1. The row already holds this exact figure in this exact currency.
  if (rawAmount != null && isFinite(rawAmount) && rawCurrency && rawCurrency.toUpperCase() === display) {
    return { value: rawAmount, exact: true };
  }

  if (!isFinite(storedUsd)) return { value: 0, exact: false };

  // 2. The row's own date. USD short-circuits: the stored value already IS the
  //    answer, so no lookup is needed and none can fail.
  if (display === 'USD') return { value: storedUsd, exact: true };
  const dated = perUsdOn(display, rateDate);
  if (dated != null) return { value: storedUsd * dated, exact: true };

  // 3. Today's rate — usable, but not what the money was.
  const spot = isFinite(spotPerUsd) && spotPerUsd > 0 ? spotPerUsd : 1;
  return { value: storedUsd * spot, exact: false };
}

/** The date a row's stored rates belong to, falling back to its transaction date. */
function rowRateDate(tx: Pick<Transaction, 'date' | 'fxRateDate'>): string {
  return tx.fxRateDate ?? tx.date;
}

/** Price per unit of `tx`, in `displayCurrency`. */
export function txPriceInDisplay(
  tx: Transaction,
  displayCurrency: string,
  spotPerUsd: number
): DisplayAmount {
  return resolve(tx.price, tx.entryAmount, tx.entryCurrency, rowRateDate(tx), displayCurrency, spotPerUsd);
}

/** The BUY/SELL trading fee of `tx`, in `displayCurrency`. Zero when there is none. */
export function txFeeInDisplay(
  tx: Transaction,
  displayCurrency: string,
  spotPerUsd: number
): DisplayAmount {
  if (!(tx.fee != null || (tx.feeUsd != null && tx.feeUsd > 0))) {
    return { value: 0, exact: true };
  }
  return resolve(tx.feeUsd ?? 0, tx.fee, tx.feeCurrency, rowRateDate(tx), displayCurrency, spotPerUsd);
}

/**
 * A TRANSFER's network fee, in `displayCurrency`.
 *
 * Transfers predate the raw-fee model and store only USD, but the amount as typed
 * is recoverable as `networkFee / fxRateUsd` — which is exactly how it was
 * written — so tier 1 still applies when the row was entered in the currency now
 * on screen.
 */
export function txNetworkFeeInDisplay(
  tx: Transaction,
  displayCurrency: string,
  spotPerUsd: number
): DisplayAmount {
  if (tx.networkFee == null) return { value: 0, exact: true };

  const recoveredRaw =
    tx.fxRateUsd != null && isFinite(tx.fxRateUsd) && tx.fxRateUsd > 0
      ? tx.networkFee / tx.fxRateUsd
      : undefined;

  return resolve(
    tx.networkFee,
    recoveredRaw,
    tx.entryCurrency,
    rowRateDate(tx),
    displayCurrency,
    spotPerUsd
  );
}

/**
 * The cash total of `tx` in `displayCurrency` — out of pocket on a BUY, in on a
 * SELL, and the network fee on a TRANSFER.
 *
 * Signed the same direction the fee moves the taxable gain, matching
 * `txnTotalUsd`. `exact` is false if EITHER leg had to fall back, because a total
 * is only as exact as its worst component.
 */
export function txTotalInDisplay(
  tx: Transaction,
  displayCurrency: string,
  spotPerUsd: number
): DisplayAmount {
  if (tx.type === 'TRANSFER') return txNetworkFeeInDisplay(tx, displayCurrency, spotPerUsd);

  const price = txPriceInDisplay(tx, displayCurrency, spotPerUsd);
  const fee = txFeeInDisplay(tx, displayCurrency, spotPerUsd);
  const gross = tx.amount * price.value;

  return {
    value: tx.type === 'BUY' ? gross + fee.value : gross - fee.value,
    exact: price.exact && fee.exact,
  };
}
